import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { detectCsvEncoding } from './csv-file-encoding';
import { DataConnection } from '@nimmsta/ng-commons-lib/models';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { DataConnectionService } from '../../../services/dataConnection.service';
import { LaddaModule } from 'angular2-ladda';

@Component({
  selector: 'app-csv',
  templateUrl: './csv.component.html',
  imports: [TranslatePipe, ErrorComponent, LaddaModule, TranslatePipe],
})
export class CsvComponent implements AfterViewInit {
  modalRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{ dataConnection: DataConnection }>,
  );
  private dataConnectionService = inject(DataConnectionService);
  private translate = inject(TranslateService);

  fileNames: string[] = [];

  fileInput: HTMLInputElement | null = null;

  @ViewChild('clearBox') clearBox!: ElementRef;

  @ViewChild('defaultTemplateLink') defaultTemplateLink!: ElementRef;

  dataConnection = this.dialogConfig.data?.dataConnection;

  loading = false;

  isSaveable = false;

  error?: HttpErrorResponse;

  ngAfterViewInit(): void {
    this.clearBox.nativeElement.checked =
      this.dataConnection?.configuration.defaultClearExisting === true;
  }

  getTemplate() {
    if (this.dataConnection?.configuration.dataTemplate) {
      const blob = new Blob([this.dataConnection?.configuration.dataTemplate], {
        type: 'text/csv',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('href', url);
      a.setAttribute(
        'download',
        `${this.dataConnection.identifier}-template.csv`,
      );
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      this.defaultTemplateLink.nativeElement.click();
    }
  }

  filesChanged(event: Event) {
    this.fileInput = event.currentTarget as HTMLInputElement;
    this.isSaveable = !!this.fileInput.files?.length;
    this.refreshNames();
  }

  refreshNames() {
    this.fileNames = [];
    if (this.fileInput) {
      const fileList: FileList | null = this.fileInput.files;
      if (fileList) {
        for (let i = 0; i < fileList.length; i++) {
          this.fileNames.push(fileList[i].name);
        }
      }
    }
  }

  removeFile(index: number) {
    if (this.fileInput && this.fileInput.files) {
      const dataTransfer = new DataTransfer();
      for (let i = 0; i < this.fileInput.files.length; i++) {
        if (i !== index) {
          dataTransfer.items.add(this.fileInput.files[i]);
        }
      }
      this.fileInput.files = dataTransfer.files;
      this.refreshNames();
    }
  }

  save() {
    this.error = undefined;
    this.loading = true;
    const promises: Promise<{ name: string; content: string }>[] = [];
    for (let i = 0; i < this.fileInput!.files!.length; i++) {
      const fileReader = new FileReader();
      const file = this.fileInput!.files![i];

      promises.push(
        new Promise((resolve) => {
          detectCsvEncoding(file).then((encoding) => {
            fileReader.onloadend = () => {
              if (fileReader.result) {
                const content = fileReader.result as string;
                resolve({ name: file.name, content });
              } else {
                resolve({ name: file.name, content: '' });
              }
            };

            fileReader.readAsText(file, encoding);
          });
        }),
      );
    }
    const clearExisting = this.clearBox.nativeElement.checked;
    Promise.all(promises).then((csvs) => {
      if (this.hasEmptyCsvsCheck(csvs)) {
        return;
      }
      csvs.forEach((csv) => {
        this.dataConnectionService
          .execute(this.dataConnection!, {
            name: csv.name,
            csv: csv.content,
            clearExisting,
          })
          .subscribe({
            next: () => {
              this.loading = false;
              this.modalRef.close();
            },
            error: (error) => {
              this.loading = false;
              this.error = error;
            },
          });
      });
    });
  }

  hasEmptyCsvsCheck(csvs: { name: string; content: string }[]): boolean {
    let hasEmpty = false;
    csvs.forEach((csv) => {
      const lines = csv.content
        .split('\n')
        .filter((line) => line.trim().length > 0);
      if (lines.length <= 1) {
        this.translate
          .get('dataconnection.csv.empty', { name: csv.name })
          .subscribe((message) => {
            this.error = { message } as HttpErrorResponse;
          });
        this.loading = false;
        hasEmpty = true;
      }
    });
    return hasEmpty;
  }
}
